/**
 * Performance page — windowed operational monitoring from admin analytics.
 */

import { useEffect, useState } from 'react';
import { useWorkspace } from '../hooks/useWorkspace';
import * as api from '../api-client';
import {
  AdminEmptyState,
  AdminPage,
  AdminPageHeader,
  AdminStatCard,
  AdminTablePanel,
} from '../components/AdminPrimitives';

type PerformanceWindow = '48h' | '7d' | '30d';
type CardStatus = 'ok' | 'warn' | 'error';

type PerformanceSummary = {
  value: number | null;
  sourceLabel: string;
};

type HourlyPerformanceRow = {
  hour: string;
  requests: number | null;
  avgLatency: number | null;
  p95Latency: number | null;
  errors: number;
  fallbacks: number;
  throttles: number;
  zeroResults: number;
  latencySampleCount: number;
};

const INTERNAL_PERFORMANCE_METRICS = new Set([
  'diagnostic.latency_sum_ms',
  'diagnostic.latency_sample_count',
]);

const PERFORMANCE_WINDOW_OPTIONS: Array<{ value: PerformanceWindow; label: string; shortLabel: string }> = [
  { value: '48h', label: 'Last 48 Hours', shortLabel: '48 hours' },
  { value: '7d', label: 'Last 7 Days', shortLabel: '7 days' },
  { value: '30d', label: 'Last 30 Days', shortLabel: '30 days' },
];

export function PerformancePage() {
  const { workspace } = useWorkspace();
  const [window, setWindow] = useState<PerformanceWindow>('48h');
  const [metrics, setMetrics] = useState<api.MetricPoint[]>([]);
  const [anomaliesOnly, setAnomaliesOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    async function load() {
      try {
        const res = await api.getPerformance(workspace!.workspace_key, window);
        if (!cancelled) {
          setMetrics(res.metrics);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof api.AdminApiError ? err.detail : 'Failed to load performance');
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [workspace, window]);

  if (!workspace) return null;

  const selectedWindow = PERFORMANCE_WINDOW_OPTIONS.find((option) => option.value === window)!;
  const windowLabel = selectedWindow.shortLabel;
  const diagnosticLatencySampleCount = computeTotal(metrics, 'diagnostic.latency_sample_count');
  const avgLatency = resolvePerformanceSummary(metrics, {
    preferredWeightedNumeratorMetric: 'diagnostic.latency_sum_ms',
    preferredWeightedDenominatorMetric: 'diagnostic.latency_sample_count',
    fallbackAverageMetric: 'conversation.avg_duration_seconds',
    preferredSourceLabel: 'Diagnostic latency',
    fallbackSourceLabel: 'Conversation duration fallback',
  });
  const errorRate = resolvePerformanceSummary(metrics, {
    preferredCountMetric: 'diagnostic.error_count',
    preferredDenominatorMetric: 'diagnostic.total_requests',
    fallbackAverageMetric: 'conversation.error_rate',
    preferredSourceLabel: 'Server errors',
    fallbackSourceLabel: 'Conversation-level errors',
  });
  const fallbackRate = resolvePerformanceSummary(metrics, {
    preferredCountMetric: 'diagnostic.fallback_count',
    preferredDenominatorMetric: 'diagnostic.total_requests',
    fallbackAverageMetric: 'conversation.fallback_rate',
    preferredSourceLabel: 'Model fallbacks',
    fallbackSourceLabel: 'Conversation-level fallbacks',
  });
  const throttleRate = resolvePerformanceSummary(metrics, {
    preferredCountMetric: 'diagnostic.throttle_count',
    preferredDenominatorMetric: 'diagnostic.total_requests',
    preferredSourceLabel: 'Rate limited',
  });
  const zeroResults = computeTotal(metrics, 'diagnostic.rag_zero_result_count');
  const guestTrialTurns = computeTotal(metrics, 'guest_mode.trial_turns');
  const guestLimitReached = computeTotal(metrics, 'guest_mode.limit_reached');
  const guestUpgrades = computeTotal(metrics, 'guest_mode.upgrades');
  const guestLimitToUpgradeRate = guestLimitReached > 0 ? guestUpgrades / guestLimitReached : 0;
  const hasGuestModeMetrics = metrics.some((m) => m.metric_name.startsWith('guest_mode.'));
  const latencyStatus = resolveLatencyStatus(avgLatency, diagnosticLatencySampleCount);
  const hourlyRows = buildHourlyRows(metrics);
  const filteredRows = anomaliesOnly ? hourlyRows.filter(isHourlyAnomaly) : hourlyRows;

  const cards: { label: string; value: string; sub: string; status: CardStatus; statusLabel?: string }[] = [
    {
      label: 'Avg Latency',
      value: formatSummaryValue(avgLatency.value, 'ms'),
      sub: `${avgLatency.sourceLabel} · ${avgLatency.value == null ? 'Unavailable' : `Last ${windowLabel}`}`,
      status: latencyStatus.status,
      statusLabel: latencyStatus.label,
    },
    {
      label: 'Error Rate',
      value: formatSummaryValue(errorRate.value, 'percent'),
      sub: `${errorRate.sourceLabel} · ${errorRate.value == null ? 'Unavailable' : `Last ${windowLabel}`}`,
      status: errorRate.value != null && errorRate.value > 0.05 ? 'error' : errorRate.value != null && errorRate.value > 0.01 ? 'warn' : 'ok',
    },
    {
      label: 'Fallback Rate',
      value: formatSummaryValue(fallbackRate.value, 'percent'),
      sub: `${fallbackRate.sourceLabel} · ${fallbackRate.value == null ? 'Unavailable' : `Last ${windowLabel}`}`,
      status: fallbackRate.value != null && fallbackRate.value > 0.1 ? 'error' : fallbackRate.value != null && fallbackRate.value > 0.03 ? 'warn' : 'ok',
    },
    {
      label: 'Throttle Rate',
      value: formatSummaryValue(throttleRate.value, 'percent'),
      sub: `${throttleRate.sourceLabel} · ${throttleRate.value == null ? 'Unavailable' : `Last ${windowLabel}`}`,
      status: throttleRate.value != null && throttleRate.value > 0.05 ? 'error' : throttleRate.value != null && throttleRate.value > 0.01 ? 'warn' : 'ok',
    },
  ];

  const cardTones = {
    ok: 'success',
    warn: 'warning',
    error: 'danger',
  } as const;

  const statusLabels = {
    ok: 'Stable',
    warn: 'Watch',
    error: 'Needs attention',
  };

  return (
    <AdminPage className="max-w-6xl">
      <AdminPageHeader
        title="Performance"
        subtitle={`Track AOAI request quality, latency, and retrieval health across the last ${windowLabel}.`}
        actions={<PerformanceWindowToggle value={window} onChange={setWindow} />}
      />

      {error && (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-[var(--status-error)] dark:border-red-800 dark:bg-red-900/20">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-36 rounded-[1.4rem] bg-[var(--surface-tertiary)] animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((card) => (
              <AdminStatCard
                key={card.label}
                label={card.label}
                value={card.value}
                sub={`${card.sub} · ${card.statusLabel ?? statusLabels[card.status]}`}
                tone={cardTones[card.status]}
              />
            ))}
          </div>

          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <AdminStatCard
              label="Zero-Result Queries"
              value={Number.isFinite(zeroResults) ? `${Math.round(zeroResults)}` : '--'}
              sub={Number.isFinite(zeroResults)
                ? `AOAI requests whose retrieval returned no useful results in the last ${windowLabel}`
                : 'Diagnostic telemetry unavailable'}
              tone={zeroResults > 0 ? 'warning' : 'default'}
            />
          </div>

          {hasGuestModeMetrics && (
            <section className="mb-8">
              <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">Guest Mode</h2>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">
                    Anonymous trial activity across the selected hourly monitoring window.
                  </p>
                </div>
                <span className="text-xs text-[var(--text-tertiary)]">Last {windowLabel}</span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <AdminStatCard
                  label="Trial Turns"
                  value={Math.round(guestTrialTurns).toString()}
                  sub={`Anonymous turns in the last ${windowLabel}`}
                  tone={guestTrialTurns > 0 ? 'success' : 'default'}
                />
                <AdminStatCard
                  label="Guest Limit Reached"
                  value={Math.round(guestLimitReached).toString()}
                  sub={`Anonymous trial limit hits in the last ${windowLabel}`}
                  tone={guestLimitReached > 0 ? 'warning' : 'default'}
                />
                <AdminStatCard
                  label="Sign-ins"
                  value={Math.round(guestUpgrades).toString()}
                  sub={`Guest upgrades in the last ${windowLabel}`}
                  tone={guestUpgrades > 0 ? 'success' : 'default'}
                />
                <AdminStatCard
                  label="Limit-to-Sign-in Rate"
                  value={formatSummaryValue(guestLimitToUpgradeRate, 'percent')}
                  sub="Sign-ins divided by limit hits"
                  tone={guestLimitToUpgradeRate > 0 ? 'success' : 'default'}
                />
              </div>
            </section>
          )}

          {hourlyRows.length > 0 ? (
            <AdminTablePanel
              title="Hourly Metrics"
              subtitle="Hour-by-hour AOAI and retrieval rollups from admin analytics."
              rightSlot={(
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setAnomaliesOnly((current) => !current)}
                    className={[
                      'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                      anomaliesOnly
                        ? 'border-[var(--accent-primary)] bg-[var(--text-primary)] text-white shadow-[0_4px_12px_rgba(15,23,42,0.18)]'
                        : 'border-[var(--border-default)] bg-white text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                    ].join(' ')}
                    aria-pressed={anomaliesOnly}
                  >
                    Anomalies only
                  </button>
                  <span>{filteredRows.length} hour{filteredRows.length === 1 ? '' : 's'}</span>
                </div>
              )}
            >
              {filteredRows.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--surface-tertiary)]">
                      <th className="px-5 py-3 text-left font-medium text-[var(--text-secondary)]">Hour</th>
                      <th className="px-5 py-3 text-right font-medium text-[var(--text-secondary)]">Requests</th>
                      <th className="px-5 py-3 text-right font-medium text-[var(--text-secondary)]">Avg Latency</th>
                      <th className="px-5 py-3 text-right font-medium text-[var(--text-secondary)]">P95</th>
                      <th className="px-5 py-3 text-right font-medium text-[var(--text-secondary)]">Errors</th>
                      <th className="px-5 py-3 text-right font-medium text-[var(--text-secondary)]">Fallbacks</th>
                      <th className="px-5 py-3 text-right font-medium text-[var(--text-secondary)]">Throttles</th>
                      <th className="px-5 py-3 text-right font-medium text-[var(--text-secondary)]">Zero Results</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr
                        key={row.hour}
                        className="border-t border-[var(--border-subtle)] bg-white/70"
                      >
                        <td className="px-5 py-3 text-[var(--text-primary)]">{formatHour(row.hour)}</td>
                        <td className="px-5 py-3 text-right font-mono text-[var(--text-primary)]">
                          {formatTableNumber(row.requests, 'count')}
                        </td>
                        <td className={cellClassName(row.avgLatency == null)}>
                          {formatTableNumber(row.avgLatency, 'ms')}
                        </td>
                        <td className={cellClassName(row.p95Latency == null)}>
                          {formatTableNumber(row.p95Latency, 'ms')}
                        </td>
                        <td className={cellClassName(row.errors === 0)}>
                          {formatTableNumber(row.errors, 'event')}
                        </td>
                        <td className={cellClassName(row.fallbacks === 0)}>
                          {formatTableNumber(row.fallbacks, 'event')}
                        </td>
                        <td className={cellClassName(row.throttles === 0)}>
                          {formatTableNumber(row.throttles, 'event')}
                        </td>
                        <td className={cellClassName(row.zeroResults === 0)}>
                          {formatTableNumber(row.zeroResults, 'event')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-5 py-8">
                  <AdminEmptyState
                    title="No anomalous hours in the selected window"
                    description={`All hourly rollups in the last ${windowLabel} are healthy or low-signal.`}
                  />
                </div>
              )}
            </AdminTablePanel>
          ) : (
            <AdminEmptyState
              title="No diagnostic telemetry available"
              description={`The workspace does not yet have AOAI and retrieval rollups for the last ${windowLabel}.`}
            />
          )}
        </>
      )}
    </AdminPage>
  );
}

function PerformanceWindowToggle({
  value,
  onChange,
}: {
  value: PerformanceWindow;
  onChange: (value: PerformanceWindow) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-full border border-[var(--border-default)] bg-white p-1 shadow-sm">
      {PERFORMANCE_WINDOW_OPTIONS.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={[
              'rounded-full px-4 py-2 text-sm font-medium transition-colors',
              active
                ? 'bg-[var(--text-primary)] text-white shadow-[0_4px_12px_rgba(15,23,42,0.18)]'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            ].join(' ')}
            aria-pressed={active}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function buildHourlyRows(metrics: api.MetricPoint[]): HourlyPerformanceRow[] {
  const grouped = new Map<string, HourlyPerformanceRow>();
  for (const metric of metrics) {
    if (!metric.metric_name.startsWith('diagnostic.')) {
      continue;
    }
    const row = grouped.get(metric.timestamp) ?? {
      hour: metric.timestamp,
      requests: null,
      avgLatency: null,
      p95Latency: null,
      errors: 0,
      fallbacks: 0,
      throttles: 0,
      zeroResults: 0,
      latencySampleCount: 0,
    };

    switch (metric.metric_name) {
      case 'diagnostic.total_requests':
        row.requests = metric.value;
        break;
      case 'diagnostic.avg_latency_ms':
        row.avgLatency = metric.value;
        break;
      case 'diagnostic.p95_latency_ms':
        row.p95Latency = metric.value;
        break;
      case 'diagnostic.error_count':
        row.errors = metric.value;
        break;
      case 'diagnostic.fallback_count':
        row.fallbacks = metric.value;
        break;
      case 'diagnostic.throttle_count':
        row.throttles = metric.value;
        break;
      case 'diagnostic.rag_zero_result_count':
        row.zeroResults = metric.value;
        break;
      case 'diagnostic.latency_sample_count':
        row.latencySampleCount = metric.value;
        break;
      default:
        break;
    }

    grouped.set(metric.timestamp, row);
  }

  return Array.from(grouped.values()).sort((left, right) => Date.parse(right.hour) - Date.parse(left.hour));
}

function isHourlyAnomaly(row: HourlyPerformanceRow): boolean {
  if (row.errors > 0 || row.fallbacks > 0 || row.throttles > 0 || row.zeroResults > 0) {
    return true;
  }

  const latencyStatus = resolveLatencyStatus(
    { value: row.avgLatency, sourceLabel: 'Diagnostic latency' },
    row.latencySampleCount,
  );
  return latencyStatus.status === 'warn' || latencyStatus.status === 'error';
}

function computeTotal(metrics: api.MetricPoint[], name: string): number {
  return metrics.filter((m) => m.metric_name === name).reduce((s, m) => s + m.value, 0);
}

function resolvePerformanceSummary(
  metrics: api.MetricPoint[],
  {
    preferredAverageMetric,
    fallbackAverageMetric,
    preferredWeightedNumeratorMetric,
    preferredWeightedDenominatorMetric,
    preferredCountMetric,
    preferredDenominatorMetric,
    preferredSourceLabel,
    fallbackSourceLabel,
  }: {
    preferredAverageMetric?: string;
    fallbackAverageMetric?: string;
    preferredWeightedNumeratorMetric?: string;
    preferredWeightedDenominatorMetric?: string;
    preferredCountMetric?: string;
    preferredDenominatorMetric?: string;
    preferredSourceLabel: string;
    fallbackSourceLabel?: string;
  },
): PerformanceSummary {
  if (preferredWeightedNumeratorMetric && preferredWeightedDenominatorMetric) {
    const weightedAverage = computeWeightedAverageOrNull(
      metrics,
      preferredWeightedNumeratorMetric,
      preferredWeightedDenominatorMetric,
    );
    if (weightedAverage != null) {
      return { value: weightedAverage, sourceLabel: preferredSourceLabel };
    }
  }

  if (preferredCountMetric && preferredDenominatorMetric) {
    const numerator = computeTotal(metrics, preferredCountMetric);
    const denominator = computeTotal(metrics, preferredDenominatorMetric);
    if (denominator > 0) {
      return { value: numerator / denominator, sourceLabel: preferredSourceLabel };
    }
  }

  if (preferredAverageMetric) {
    const average = computeAverageOrNull(metrics, preferredAverageMetric);
    if (average != null) {
      return { value: average, sourceLabel: preferredSourceLabel };
    }
  }

  if (fallbackAverageMetric) {
    const average = computeAverageOrNull(metrics, fallbackAverageMetric);
    if (average != null) {
      return {
        value: average,
        sourceLabel: fallbackSourceLabel ?? preferredSourceLabel,
      };
    }
  }

  return { value: null, sourceLabel: preferredSourceLabel };
}

function computeAverageOrNull(metrics: api.MetricPoint[], name: string): number | null {
  const vals = metrics.filter((m) => m.metric_name === name).map((m) => m.value);
  if (vals.length === 0) return null;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

function computeWeightedAverageOrNull(
  metrics: api.MetricPoint[],
  numeratorMetric: string,
  denominatorMetric: string,
): number | null {
  const numerator = computeTotal(metrics, numeratorMetric);
  const denominator = computeTotal(metrics, denominatorMetric);
  if (denominator <= 0) return null;
  return numerator / denominator;
}

function resolveLatencyStatus(
  summary: PerformanceSummary,
  diagnosticSampleCount: number,
): { status: CardStatus; label?: string } {
  if (summary.value == null) {
    return { status: 'ok' };
  }

  if (summary.sourceLabel === 'Diagnostic latency' && diagnosticSampleCount < 25) {
    return {
      status: 'ok',
      label: `Low sample size (${Math.round(diagnosticSampleCount)} sample${diagnosticSampleCount === 1 ? '' : 's'})`,
    };
  }

  if (summary.value > 8000) {
    return { status: 'error' };
  }
  if (summary.value > 5000) {
    return { status: 'warn' };
  }
  return { status: 'ok' };
}

function formatSummaryValue(value: number | null, kind: 'ms' | 'percent'): string {
  if (value == null) return '--';
  if (kind === 'ms') return `${Math.round(value)}ms`;
  return `${(value * 100).toFixed(2)}%`;
}

function formatHour(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatTableNumber(
  value: number | null,
  kind: 'ms' | 'count' | 'event',
): string {
  if (value == null) return '--';
  if (kind === 'ms') return `${Math.round(value)}ms`;
  if (kind === 'event' && value === 0) return '--';
  return value.toFixed(0);
}

function cellClassName(isMuted: boolean): string {
  return [
    'px-5 py-3 text-right font-mono',
    isMuted ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-primary)]',
  ].join(' ');
}

export const _performancePageTestExports = {
  buildHourlyRows,
  isHourlyAnomaly,
  resolveLatencyStatus,
  INTERNAL_PERFORMANCE_METRICS,
};
